<template>
  <Card card_class="backdrop-blur-none">
    <router-link to="/product" id="back-button"> &#10094; Back </router-link>

    <AlertSuccess
      v-if="alert_success"
      :text="alert_text"
      alert_class="w-full mt-8 relative"
    />
    <AlertDanger
      v-if="alert_danger"
      :text="alert_text"
      @closeAlertDanger="hideAlertDanger"
      alert_class="w-full mt-8 relative"
    />
    <section
      class="p-2 md:p-6 flex justify-center items-center w-full max-w-5xl mx-auto min-h-[calc(100dvh-100px)] pb-12"
    >
      <div class="grid grid-cols-12 md:gap-10 mb-8 w-full">
        <h1 class="col-span-12 font-bold text-2xl">
          {{ isEdit ? "Edit Product" : "Create New Product" }}
        </h1>
        <Card
          card_class="backdrop-blur-lg col-span-12 lg:col-span-6 mt-4 md:mt-0"
          card_body_class=""
        >
          <InputForm
            id="product-name"
            v-model="product.products_name"
            input_class="w-full mb-4"
            label="Product Name"
            label_class="mb-1 block text-start"
            type="text"
            placeholder="e.g. Sulam Alis"
          />
          <Select
            option_placeholder="Category"
            :option_placeholder_selected="true"
            :option_placeholder_disabled="true"
            select_class="w-full mb-4"
            valueKey="id"
            labelKey="name"
            :options="all_categories"
            v-model="product.category_id"
            @update:modelValue="selectCategory"
          />
          <div class="flex flex-wrap items-center gap-1 w-full mb-4">
            <Card
              v-for="(category, index) in selected_categories"
              :key="index"
              card_class="bg-white/10"
              card_body_class="flex gap-2 items-center text-sm"
            >
              {{ category.name }}
              <button @click="deleteCategory(category.id)" id="cancel-button">
                <XMarkIcon class="h-5 w-5" />
              </button>
            </Card>
          </div>
          <InputRupiah
            id="product-price"
            v-model="product.price"
            input_class="w-full mb-4"
            label="Price"
            label_class="mb-1 block text-start"
            type="number"
            placeholder="e.g. 10000"
          />
          <TextArea
            label="Description"
            rows="5"
            v-model="product.description"
          />
        </Card>
        <div class="col-span-12 lg:col-span-6 mt-4 md:mt-0">
          <Card
            card_class="product-reminders backdrop-blur-lg"
            card_title="Reminders"
          >
            <p
              class="bg-red-500 text-white font-medium text-sm px-4 p-2 rounded-lg my-2"
              v-if="errorMessageReminder"
            >
              {{ errorMessageReminder }}
            </p>
            <div class="reminder-item">
              <div class="flex flex-col md:flex-row gap-2">
                <div class="w-full md:w-1/4">
                  <InputText
                    id="retouch-term"
                    v-model="product_reminders.days"
                    input_class="w-full font-bold !text-lg"
                    label_class="block text-start"
                    type="text"
                    placeholder="e.g. 10"
                    span_class="right-0 bottom-[2px] !font-light !tracking-tight"
                    span_text="days"
                    label="Remind in"
                  />
                </div>
                <div class="w-full md:w-3/4">
                  <TextArea
                    id="reminder-message"
                    :label="'Reminder Message'"
                    rows="1"
                    v-model="product_reminders.message"
                  />
                </div>
              </div>
              <div class="mt-2 flex justify-end">
                <Button
                  @click="addReminder"
                  button_id="add-reminder"
                  button_text="&#43; Add Reminder"
                  button_class="text-xs"
                />
              </div>
            </div>
            <div class="flex flex-col gap-2 mt-4">
              <Card
                v-for="(reminder, index) in product.reminders"
                :key="index"
                card_body_class="flex w-full gap-4 text-sm py-1 px-2"
                card_class="backdrop-blur-lg"
              >
                <p>
                  Reminder {{ index + 1 }}, in
                  <span class="text-lg font-bold">{{ reminder.days }}</span>
                  days:
                  <span class="text-lg font-bold"
                    >"{{ reminder.message }}"</span
                  >
                </p>
                <button
                  @click="deleteReminder(reminder.message)"
                  class="absolute right-1 top-1"
                >
                  <XMarkIcon class="h-5 w-5" />
                </button>
              </Card>
            </div>
          </Card>
          <Card
            card_class="backdrop-blur-lg product-coupons mt-4"
            card_title="Promo Coupons"
          >
            <Select
              option_placeholder="Add Promo Coupon"
              select_class="w-full"
              valueKey="id"
              labelKey="name"
              :options="all_coupons"
              @update:modelValue="selectCoupon"
            />
            <div class="flex flex-wrap items-center gap-1 w-full mt-4">
              <Card
                v-for="(coupon, index) in selected_coupons"
                :key="index"
                card_class="bg-white/10"
                card_body_class="flex gap-2 items-center text-sm"
              >
                {{ coupon.name ? coupon.name : coupon.coupon_name }}
                <button @click="deleteCoupon(coupon.id)" id="cancel-button">
                  <XMarkIcon class="h-5 w-5" />
                </button>
              </Card>
            </div>
          </Card>
        </div>
        <div class="col-span-12 flex justify-end mt-4">
          <div class="flex items-center gap-4">
            <CancelButton to_url="/product" />
            <span>
              <SubmitButton
                @click="submit()"
                :is_loading="loadingSubmit"
                :is_disabled="loadingSubmit"
                id="submit"
                button_text="Save Product"
              />
            </span>
          </div>
        </div>
      </div>
    </section>
  </Card>
</template>

<script>
import Card from "@/components/organisms/Card.vue";
import InputForm from "@/components/form/InputForm.vue";
import InputText from "@/components/form/InputText.vue";
import InputRupiah from "@/components/form/InputRupiah.vue";
import TextArea from "@/components/form/TextArea.vue";
import AlertDanger from "@/components/alerts/AlertDanger.vue";
import AlertSuccess from "@/components/alerts/AlertSuccess.vue";
import Select from "@/components/form/Select.vue";
import Button from "@/components/buttons/Button.vue";
import ButtonRouter from "@/components/buttons/ButtonRouter.vue";
import CancelButton from "@/components/buttons/CancelButton.vue";
import { mapGetters, mapActions } from "vuex";
import { XMarkIcon } from "@heroicons/vue/24/solid";
import SubmitButton from "@/components/form/SubmitButton.vue";

export default {
  name: "AddEditProduct",
  props: ["id"],
  components: {
    Card,
    AlertSuccess,
    AlertDanger,
    Button,
    ButtonRouter,
    CancelButton,
    Select,
    InputForm,
    InputRupiah,
    InputText,
    TextArea,
    XMarkIcon,
    SubmitButton,
  },
  data() {
    return {
      isEdit: false,
      alert_success: false,
      alert_danger: false,
      alert_text: "",
      errorMessageReminder: "",

      loadingSubmit: false,

      product: {
        id: null,
        products_name: "",
        price: "",
        description: "",
        categories: [],
        reminders: [],
        promos: [],
      },
      product_reminders: [{ days: null, message: "" }],

      coupons: [],
      all_coupons: [],
      selected_coupons: [],
      all_categories: [],
      selected_categories: [],
    };
  },
  async created() {
    await this.$store.dispatch("coupons/fetchAllCoupons");
    await this.$store.dispatch("categories/fetchCategories");
    // Mengambil semua data dari store coupons dan menambahkan properti coupon_id
    this.all_coupons = this.allCoupon.map((coupon) => ({
      ...coupon,
      coupon_id: coupon.id,
    }));

    this.all_categories = this.allCategories;

    if (this.id) {
      this.isEdit = true;
      const response = await this.fetchProductById(this.id);
      const productData = response.data;
      // console.log('edit produk by id: ', productData);

      this.product.id = productData.id;
      this.product.products_name = productData.products_name;
      this.product.price = parseInt(productData.price);
      this.product.category_id = productData.category_id;
      this.product.description = productData.description;
      this.product.promos = productData.promos;
      this.product.reminders = productData.reminders;
      this.product.categories = productData.categories;

      this.selected_coupons = this.all_coupons
        .filter((coupon) =>
          productData.promos.some(
            (promo) => promo.coupon_id === coupon.coupon_id
          )
        )
        .map((coupon) => ({
          ...coupon,
          id: coupon.coupon_id, // Menambahkan properti id yang sama dengan coupon_id
        }));

      this.all_coupons = this.all_coupons.filter(
        (coupon) =>
          !this.selected_coupons.some(
            (selected) => selected.coupon_id == coupon.coupon_id
          )
      );
      // console.log('selected_coupons: ', this.selected_coupons);
      // console.log('all_coupons: ', this.all_coupons);
      if (productData.categories) {
        this.selected_categories = this.all_categories.filter((category) =>
          productData.categories.some((selected) => selected.id == category.id)
        );

        this.all_categories = this.all_categories.filter(
          (category) =>
            !this.selected_categories.some(
              (selected) => selected.id == category.id
            )
        );
      } else {
        this.product.categories = [];
      }
      // console.log('selected_categories: ', this.selected_categories);
      // console.log('all_categories: ', this.all_categories);
    }
  },
  computed: {
    ...mapGetters("coupons", ["allCoupon", "pagination"]),
    ...mapGetters("categories", ["allCategories"]),
  },
  methods: {
    ...mapActions("products", [
      "createProduct",
      "fetchProductById",
      "updateProduct",
    ]),

    addReminder() {
      const reminder = {
        days: parseInt(this.product_reminders.days),
        message: this.product_reminders.message,
      };
      if (!reminder.days || !reminder.message) {
        this.errorMessageReminder =
          "Reminder days and message cannot be empty!";
        setTimeout(() => {
          this.errorMessageReminder = "";
        }, 2000);
        return;
      }
      this.product.reminders.push(reminder);

      // console.log('produk setelah tambah reminder: ', this.product);
      this.product_reminders.days = null;
      this.product_reminders.message = "";
    },
    deleteReminder(message) {
      // console.log('pesan: ', message);
      this.product.reminders = this.product.reminders.filter(
        (reminder) => reminder.message != message
      );
    },
    selectCoupon(couponId) {
      const selectedCoupon = this.all_coupons.find(
        (coupon) => coupon.id == couponId
      );
      if (
        selectedCoupon &&
        !this.selected_coupons.some((coupon) => coupon.id == couponId)
      ) {
        this.selected_coupons.push(selectedCoupon);

        this.all_coupons = this.all_coupons.filter(
          (coupon) => coupon.id != couponId
        );
        const product_promo = {
          name: selectedCoupon.name,
          coupon_id: selectedCoupon.id,
          on_percentage: selectedCoupon.on_percentage,
          // 'branch_id': null,
          // 'expire_days': selectedCoupon.expiry_period_per_branch,
          // 'max_usage': selectedCoupon.max_usage,
          // 'discount': selectedCoupon.discount,
        };

        this.product.promos.push(product_promo);
      }
      // console.log("kupon dipilih: ", selectedCoupon);
      // console.log('produk setelah memilih kupon: ', this.product);
    },
    deleteCoupon(couponId) {
      // Hapus kupon dari selected_coupons
      const removedCoupon = this.selected_coupons.find(
        (coupon) => coupon.id == couponId
      );
      this.selected_coupons = this.selected_coupons.filter(
        (coupon) => coupon.id != couponId
      );

      // Menambah kupon kembali ke all_coupons
      if (removedCoupon) {
        this.all_coupons.push(removedCoupon);
      }

      this.product.promos = this.product.promos.filter(
        (coupon) => coupon.coupon_id != couponId
      );
    },
    selectCategory(categoryId) {
      const selectedCategory = this.all_categories.find(
        (category) => category.id == categoryId
      );
      if (
        selectedCategory &&
        !this.selected_categories.some((category) => category.id == categoryId)
      ) {
        this.selected_categories.push(selectedCategory);

        this.all_categories = this.all_categories.filter(
          (category) => category.id != categoryId
        );
        const product_category = {
          name: selectedCategory.name,
          id: +selectedCategory.id,
        };

        this.product.categories.push(product_category);
      }
    },
    deleteCategory(categoryId) {
      const removedCategory = this.selected_categories.find(
        (category) => category.id == categoryId
      );
      this.selected_categories = this.selected_categories.filter(
        (category) => category.id != categoryId
      );

      if (removedCategory) {
        this.all_categories.push(removedCategory);
      }

      this.product.categories = this.product.categories.filter(
        (category) => category.id != categoryId
      );
    },
    async submit() {
      // console.log('submit produk: ', this.product);
      if (!this.product.products_name) {
        this.showAlert("failed", "Product name cannot be empty!");
        return;
      }
      if (!this.product.price) {
        this.showAlert("failed", "Product price cannot be empty!");
        return;
      }
      if (this.id) {
        try {
          this.product.price = parseInt(this.product.price);
          // console.log('produk to update: ', this.product);
          this.loadingSubmit = true;
          await this.updateProduct(this.product);
          this.loadingSubmit = false;
          this.showAlert("success", "Successfully updated product!");
          setTimeout(() => {
            this.$router.push({ name: "Product" });
          }, 1000);
        } catch (error) {
          console.error(error);
          this.showAlert("failed", "Failed to update product!", error);
        }
      } else {
        try {
          this.loadingSubmit = true;
          await this.createProduct(this.product);
          this.loadingSubmit = false;

          this.showAlert("success", "Product added successfully!");
          setTimeout(() => {
            this.$router.push({ name: "Product" });
          }, 1000);
        } catch (error) {
          console.error(error);
          this.showAlert("failed", "Failed to create product!", error);
        }
      }
    },
    hideAlertDanger() {
      this.alert_danger = false;
      this.alert_text = "";
    },
    showAlert(type, text) {
      if (type == "success") {
        this.alert_success = true;
        this.alert_text = text;
      } else {
        this.alert_danger = true;
        this.alert_text = text;
      }
      setTimeout(() => {
        this.alert_success = false;
        this.alert_danger = false;
        this.alert_text = "";
      }, 2000);
    },
  },
};
</script>
